import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CreateApplication.css';

const CreateApplication = () => {
  const [applications, setApplications] = useState([]);
  const [providerTools, setProviderTools] = useState([]);
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [functionToolJson, setFunctionToolJson] = useState('');
  const [useCodeInterpreter, setUseCodeInterpreter] = useState(false);
  const [useFileSearch, setUseFileSearch] = useState(false);
  const [jsonSchemaInput, setJsonSchemaInput] = useState('');
  const [selectedAuth, setSelectedAuth] = useState('');
  const [editingApp, setEditingApp] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [userAuthentications, setUserAuthentications] = useState([]);
  const [selectedSubmodels, setSelectedSubmodels] = useState([]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = storedUser?.user?.id || storedUser?.id;

    const fetchData = async () => {
      try {
        const toolsRes = await axios.get('/api/tools');
        const providers = toolsRes.data.filter(t => t.type === 'provider');
        setProviderTools(providers);

        if (userId) {
          const authRes = await axios.get(`/api/authentications`);
          setUserAuthentications(authRes.data || []);

          const appRes = await axios.get(`/api/application/trigger`);
          setApplications(appRes.data || []);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  const handleProviderChange = (selectedName) => {
    setProvider(selectedName);
    setModel('');

    const selectedTool = providerTools.find(tool => tool.name === selectedName);
    setSelectedSubmodels(selectedTool?.submodels || []);
  };

  const handleAdd = async () => {                 
    const storedData = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = storedData.user?.id;
    if (!userId || !name || !provider || !model || !instructions || !jsonSchemaInput) {
      return alert("Please fill all required fields");
    }

    let toolsConfig = [];
    if (useCodeInterpreter) toolsConfig.push({ type: 'code_interpreter' });
    if (useFileSearch) toolsConfig.push({ type: 'file_search' });

    if (functionToolJson.trim()) {
      try {
        const parsed = JSON.parse(functionToolJson);
        if (Array.isArray(parsed)) parsed.forEach(f => f.type === 'function' && toolsConfig.push(f));
        else if (parsed.type === 'function') toolsConfig.push(parsed);
      } catch(e) {
        return alert('Invalid function tool JSON');
      }
    }

    const payload = {
      userId,
      name,
      provider,
      model,
      instructions,
      tools: toolsConfig,
      authentication: selectedAuth,
      additionalJsonSchema: jsonSchemaInput,
      functionToolJson,
      codeInterpreter: useCodeInterpreter,
      fileSearch: useFileSearch
    };

    try {
      const res = editingApp
        ? await axios.put(`/api/applications/${editingApp.id}`, payload)
        : await axios.post('/api/applications', payload);

      const updated = res.data;
      setApplications(prev => editingApp ? prev.map(app => (app.id === updated.id ? updated : app)) : [...prev, updated]);

      setName(''); setProvider(''); setModel(''); setInstructions('');
      setFunctionToolJson(''); setUseCodeInterpreter(false); setUseFileSearch(false);
      setJsonSchemaInput(''); setSelectedAuth(''); setEditingApp(null); setShowForm(false);
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    }
  };

  const filteredAuths = provider ? userAuthentications.filter(a => a.tool.toLowerCase() === provider.toLowerCase()) : [];

  return (
    <div className="app-container">
      <div className="app-header">
        <h2>Applications</h2>
        <button className="button" onClick={() => setShowForm(true)}>+ Create Application</button>
      </div>

      {showForm ? (
        <form className="app-form">
          <h3>{editingApp ? 'Edit' : 'New'} Assistant</h3>
          <input className="textinput" type="text" placeholder="Assistant Name" value={name} onChange={(e) => setName(e.target.value)} disabled={!!editingApp} />

          <select className="textinput" value={provider} onChange={(e) => handleProviderChange(e.target.value)}>
            <option value="">Select Provider</option>
            {providerTools.map((prov, idx) => <option key={idx} value={prov.name}>{prov.name}</option>)}
          </select>

          {provider && filteredAuths.length > 0 && (
            <select className="textinput" value={selectedAuth} onChange={(e) => setSelectedAuth(e.target.value)}>
              <option value="">Select Authentication</option>
              {filteredAuths.map((auth, i) => <option key={i} value={auth.name}>{auth.name}</option>)}
            </select>
          )}

          {selectedSubmodels.length > 0 && (
            <div className="model-select">
              <label>Model:</label>
              <div className="capsule-list">
                {selectedSubmodels.map((m) => (
                  <span key={m} className={`capsule ${model === m ? 'selected' : ''}`} onClick={() => setModel(m)}>{m}</span>
                ))}
              </div>
            </div>
          )}

          <textarea className="textarea" placeholder="Instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} />

          <div className="tools-box">
            <h4>Tools</h4>
            <div className="tool-options">
              <label><input type="checkbox" checked={useCodeInterpreter} onChange={(e) => setUseCodeInterpreter(e.target.checked)} /> Code Interpreter</label>
              <label><input type="checkbox" checked={useFileSearch} onChange={(e) => setUseFileSearch(e.target.checked)} /> File Search</label>
            </div>
            <textarea className="textarea" placeholder="Function Tool JSON" rows="4" value={functionToolJson} onChange={(e) => setFunctionToolJson(e.target.value)} />
          </div>

          <label>Additional JSON Schema</label>
          <textarea className="textarea" value={jsonSchemaInput} onChange={(e) => setJsonSchemaInput(e.target.value)} />

          <div className="button-group">
            <button className="button" type="button" onClick={handleAdd}>{editingApp ? 'Update' : 'Add'}</button>
            <button className="button cancel" type="button" onClick={() => { setShowForm(false); setEditingApp(null); }}>Cancel</button>
          </div>
        </form>
      ) : (
        <div className="list-section">
          <h3>Applications List</h3>
          {applications.map((app, idx) => (
            <div key={idx} className="app-card">
              <p><strong>Name:</strong> {app.name}</p>
              <p><strong>Provider:</strong> {app.provider}</p>
              <p><strong>Model:</strong> {app.model}</p>
              <p><strong>Authentication:</strong> {app.authentication}</p>
              <p><strong>Additional Json Schema:</strong> {app.additionaljsonschema}</p>
              <p><strong>Instructions:</strong> {app.instructions || app.system_message}</p>
              <pre className="json-block">{app.json_input || app.json_input || 'N/A'}</pre>
              <p><strong>Code Interpreter:</strong> {app.code_interpreter = 'True' ? '✅' : '❌'}</p>
              <p><strong>File Search:</strong> {app.file_search = 'True' ? '✅' : '❌'}</p>
              <button className="button" onClick={() => {
                setEditingApp(app);
                setName(app.name);
                setProvider(app.provider);
                setModel(app.model);
                setInstructions(app.instructions || '');
                setFunctionToolJson(app.functionToolJson || '');
                setJsonSchemaInput(app.jsonInput || '');
                setUseCodeInterpreter(!!app.codeInterpreter);
                setUseFileSearch(!!app.fileSearch);
                setSelectedAuth(app.authentication || '');
                setShowForm(true);
              }}>✏️ Edit</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreateApplication;